import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { TableHelperService } from 'app/shared/tables/table-helper.service';
import {
    HttpService,
    FiltersService,
    SettingsProvider,
    FilterOutputModel,
    TableResponseQuery, GeneralService,
} from 'app/core';
import {
    ProductDescriptionQuery,
    MainProductListQuery,
    ProductStatusEnum,
    ProductStorePricesListQuery,
    ProductLogicsBrowserList,
    SetPriceModel,
} from '../model/model';
import { environment as env } from 'environments/environment';
import { map } from 'rxjs/operators';

@Injectable()
export class ProductsDataService {

    productId = null;

    constructor(
        private http: HttpService,
        private tableHelperService: TableHelperService,
        public filterService: FiltersService,
        public settingsService: SettingsProvider,
        private generalService: GeneralService
    ) {}

    fetch(
        top: number = 100,
        skip: number = 0,
        sortBy: string = 'Id'
    ): Observable<ProductDescriptionQuery[]> {
        const url = `${this.settingsService.configuration.url}${env.ProductsDataService.apiUrlGetList}`;
        let params = new HttpParams();

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        return this.http
            .getWithParams(url, params)
            .pipe(map((response) => response.body.value));
    }

    // fetchData for tables
    // models: MainProductListQuery
    fetchData(
        top: number = 100,
        skip: number = 0,
        sortBy: string = 'Id',
        filter: string = '',
        selectFilters: FilterOutputModel[]
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.ProductsDataService.apiUrlGetList}`;
        let params = new HttpParams();
        params = params.set('$count', 'true');

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        // logic to set filter values
        if (filter || (selectFilters && !!selectFilters.length)) {
            const mainFilterString = this.filterService.setFilters(
                filter,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filter,
                    MainProductListQuery.tableInfo().columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    MainProductListQuery.tableInfo().columns
                )
            );
            params = params.set('$filter', mainFilterString);
        }

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = this.generalService.toUpperCamelCaseObj(response.body.items);

                return new TableResponseQuery(
                    items,
                    response.body.count
                );
            }),
            map((response) => {
                response.items = response.items.map(
                    (product) => {
                        return {
                            ...product,
                            Status: ProductStatusEnum[product.Status],
                            CategoryName: this.generalService.getCategoryNameLevel(product)
                        };
                    }
                );
                return response;
            })
        );
    }

    // fetch data for inner table
    // models:
    fetchInnerData(
        top: number = 100,
        skip: number = 0,
        sortBy: string = '',
        filter: string = '',
        selectFilters: FilterOutputModel[]
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.ProductsDataService.apiUrlGetStorePrices}/${this.productId}/prices`;
        let params = new HttpParams();
        params = params.set('$count', 'true');

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        // logic to set filter values
        if (filter || (selectFilters && !!selectFilters.length)) {
            const mainFilterString = this.filterService.setFilters(
                filter,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filter,
                    ProductStorePricesListQuery.tableInfo().columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    ProductStorePricesListQuery.tableInfo().columns
                )
            );
            params = params.set('$filter', mainFilterString);
        }

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = this.generalService.toUpperCamelCaseObj(response.body.items);

                return new TableResponseQuery(
                    items,
                    response.body.count
                );
            })
        );
    }

    fetchProductLogicsData(
        top: number = 100,
        skip: number = 0,
        sortBy: string = 'Id',
        filter: string = '',
        selectFilters: FilterOutputModel[]
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.ProductsDataService.apiUrlProductLogicsProductsList}`;
        let params = new HttpParams();
        params = params.set('$count', 'true');

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        // logic to set filter values
        if (filter || (selectFilters && !!selectFilters.length)) {
            const mainFilterString = this.filterService.setFilters(
                filter,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filter,
                    ProductLogicsBrowserList.tableInfo().columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    ProductLogicsBrowserList.tableInfo().columns
                )
            );
            params = params.set('$filter', mainFilterString);
        }

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = this.generalService.toUpperCamelCaseObj(response.body.items);

                return new TableResponseQuery(
                    items.map((item) => {
                        return {
                            ...item,
                            CategoryName: this.generalService.getCategoryNameLevel(item)
                        };
                    }),
                    response.body.count
                );
            })
        );
    }

    createProductLogicRelation(command: any): Observable<any> {
        // using update method for create & update & delete
        // using update instead of create because SKU already in use are overriden without errors
        const url = `${this.settingsService.configuration.url}${env.ProductsDataService.apiUrlProductLogicsMainRelationCreate}`;
        return this.http.put(url, command);
    }

    getById(id: number): Observable<ProductDescriptionQuery> {
        const url = `${this.settingsService.configuration.url}${env.ProductsDataService.apiUrlGetDetails}/${id}`;
        const params = new HttpParams();

        return this.http
            .getWithParams(url, params)
            .pipe(map((response) => this.generalService.toUpperCamelCaseObj(response.body)));
    }

    setProductAsMain(prodId: number): Observable<any> {
        // tslint:disable-next-line: max-line-length
        const url = `${this.settingsService.configuration.url}${env.ProductsDataService.apiUrlProductLogicsInitMain}/${prodId}/SetMainProduct`;
        return this.http.post(url, {});
    }

    unsetProductAsMain(prodId: number): Observable<any> {
        // tslint:disable-next-line: max-line-length
        const url = `${this.settingsService.configuration.url}${env.ProductsDataService.apiUrlProductLogicsInitMain}/${prodId}/UnsetMainProduct`;
        return this.http.post(url, {});
    }

    changeProductPrice(command: SetPriceModel) {
        const url = `${this.settingsService.configuration.url}${env.ProductsDataService.apiUrlSetPrice}`;
        return this.http.post(url, command).pipe(map((response) => response));
    }

    getMainProductDependencies(prodId: number): Observable<any> {
        // tslint:disable-next-line: max-line-length
        const url = `${this.settingsService.configuration.url}${env.ProductsDataService.apiUrlProductLogicsMainDependenciesList}/${prodId}/dependencies`;
        let params = new HttpParams();
        params = params.set('$count', 'true');
        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = this.generalService.toUpperCamelCaseObj(response.body.items);

                return new TableResponseQuery(
                    items.map((item) => {
                        return {
                            ...item,
                            CategoryName: this.generalService.getCategoryNameLevel(item)
                        };
                    }),
                    response.body.count
                );
            })
        );
    }

    getMainForDependent(prodId: number): Observable<any> {
        // tslint:disable-next-line: max-line-length
        const url = `${this.settingsService.configuration.url}${env.ProductsDataService.apiUrlProductLogicsMainForDependent}/${prodId}/main`;
        return this.http.get(url).pipe(
            map((response) => {
                const res = this.generalService.toUpperCamelCaseObj(response);

                return res;
            })
        );
    }
}
